import * as api from "@/lib/api";
import { GENERAL_ADMIN_RATE_INHOUSE, MATERIAL_ADMIN_RATE } from "@/lib/cost/admin";
import { DEFAULT_ALLOWANCE } from "@/lib/cost/labor";
import { CONSTANTS, DENSITIES, PROCESSES, laborRateOf } from "@/lib/cost/master";
import type {
  CncLaborRate,
  CncMachineExpense,
  CncRawMaterial,
  CncSettings,
  CncSurfaceTreatment,
  Customer,
  Material,
  MonthlyPoint,
  PriceHistoryPoint,
  Process,
  Quote,
  Staff,
} from "@/lib/data";
import { sortByKo } from "@/lib/sort";
// hooks.ts — lightweight data-fetching hooks built on useState/useEffect only.
// (react-query / react-router are intentionally banned in this project.)
import { useCallback, useEffect, useMemo, useState } from "react";

export interface Resource<T> {
  data: T;
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
}

// Generic async resource. `fetcher` is wrapped in useCallback by callers (or is
// a stable module-level fn) so the effect re-runs only when inputs change.
function useResource<T>(fetcher: () => Promise<T>, initial: T): Resource<T> {
  const [data, setData] = useState<T>(initial);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await fetcher());
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [fetcher]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [fetcher]);

  return { data, loading, error, reload };
}

// ----- Quotes ---------------------------------------------------------------
export function useQuotes(): Resource<Quote[]> {
  return useResource(api.getQuotes, []);
}

export function useQuote(id: string | null): Resource<Quote | null> {
  const fetcher = useCallback(() => (id ? api.getQuote(id) : Promise.resolve(null)), [id]);
  return useResource(fetcher, null);
}

// Quote mutations — plain async functions; callers reload() the relevant hook.
export const createQuote = api.createQuote;
export const updateQuote = api.updateQuote;
export const updateQuoteStatus = api.updateQuoteStatus;
export const deleteQuote = api.deleteQuote;

// ----- Customers ------------------------------------------------------------
export interface CrudResource<T> extends Resource<T[]> {
  create: (item: Omit<T, "id"> & { id?: string }) => Promise<T>;
  update: (id: string, patch: Partial<T>) => Promise<T>;
  remove: (id: string) => Promise<void>;
}

// Wrap a mutation so it runs, triggers a reload of the list, then resolves with
// the server's result.
async function withReload<R>(op: Promise<R>, reload: () => Promise<void>): Promise<R> {
  const result = await op;
  await reload();
  return result;
}

export function useCustomers(): CrudResource<Customer> {
  const res = useResource(api.getCustomers, [] as Customer[]);
  // 서버는 문서 순서 그대로 준다 — 드롭다운/표가 가나다순이 되도록 여기서 한 번만 정렬.
  // useMemo 로 배열 identity 를 고정해야 소비처 useMemo 의존성이 매 렌더 깨지지 않는다.
  const data = useMemo(() => sortByKo(res.data, (c) => c.name), [res.data]);
  return {
    ...res,
    data,
    create: (c) => withReload(api.createCustomer(c), res.reload),
    update: (id, patch) => withReload(api.updateCustomer(id, patch), res.reload),
    remove: (id) => withReload(api.deleteCustomer(id), res.reload),
  };
}

export function useMaterials(): CrudResource<Material> {
  const res = useResource(api.getMaterials, [] as Material[]);
  return {
    ...res,
    create: (m) => withReload(api.createMaterial(m), res.reload),
    update: (id, patch) => withReload(api.updateMaterial(id, patch), res.reload),
    remove: (id) => withReload(api.deleteMaterial(id), res.reload),
  };
}

export function useProcesses(): CrudResource<Process> {
  const res = useResource(api.getProcesses, [] as Process[]);
  return {
    ...res,
    create: (p) => withReload(api.createProcess(p), res.reload),
    update: (id, patch) => withReload(api.updateProcess(id, patch), res.reload),
    remove: (id) => withReload(api.deleteProcess(id), res.reload),
  };
}

// Staff hook exposes the raw {id,name} objects plus a `names` convenience list.
export interface StaffResource extends CrudResource<Staff> {
  names: string[];
}
export function useStaff(): StaffResource {
  const res = useResource(api.getStaff, [] as Staff[]);
  return {
    ...res,
    names: res.data.map((s) => s.name),
    create: (s) => withReload(api.createStaff(s), res.reload),
    update: (id, patch) => withReload(api.updateStaff(id, patch), res.reload),
    remove: (id) => withReload(api.deleteStaff(id), res.reload),
  };
}

// ----- CNC 마스터 -------------------------------------------------------------
export function useCncRawMaterials(): CrudResource<CncRawMaterial> {
  const res = useResource(api.getCncRawMaterials, [] as CncRawMaterial[]);
  const data = useMemo(() => sortByKo(res.data, (m) => m.name), [res.data]);
  return {
    ...res,
    data,
    create: (m) => withReload(api.createCncRawMaterial(m), res.reload),
    update: (id, patch) => withReload(api.updateCncRawMaterial(id, patch), res.reload),
    remove: (id) => withReload(api.deleteCncRawMaterial(id), res.reload),
  };
}

export function useCncSurfaceTreatments(): CrudResource<CncSurfaceTreatment> {
  const res = useResource(api.getCncSurfaceTreatments, [] as CncSurfaceTreatment[]);
  return {
    ...res,
    create: (s) => withReload(api.createCncSurfaceTreatment(s), res.reload),
    update: (id, patch) => withReload(api.updateCncSurfaceTreatment(id, patch), res.reload),
    remove: (id) => withReload(api.deleteCncSurfaceTreatment(id), res.reload),
  };
}

export function useCncLaborRates(): CrudResource<CncLaborRate> {
  const res = useResource(api.getCncLaborRates, [] as CncLaborRate[]);
  // 공정 드롭다운 라벨은 name 이 아니라 process 필드.
  const data = useMemo(() => sortByKo(res.data, (r) => r.process), [res.data]);
  return {
    ...res,
    data,
    create: (r) => withReload(api.createCncLaborRate(r), res.reload),
    update: (id, patch) => withReload(api.updateCncLaborRate(id, patch), res.reload),
    remove: (id) => withReload(api.deleteCncLaborRate(id), res.reload),
  };
}

export function useCncMachineExpenses(): CrudResource<CncMachineExpense> {
  const res = useResource(api.getCncMachineExpenses, [] as CncMachineExpense[]);
  return {
    ...res,
    create: (e) => withReload(api.createCncMachineExpense(e), res.reload),
    update: (id, patch) => withReload(api.updateCncMachineExpense(id, patch), res.reload),
    remove: (id) => withReload(api.deleteCncMachineExpense(id), res.reload),
  };
}

// 설정 singleton — 초기값은 정적 상수 미러(서버 응답 전에도 즉시 렌더 가능).
const CNC_SETTINGS_FALLBACK: CncSettings = {
  allowance: DEFAULT_ALLOWANCE,
  generalAdminRate: GENERAL_ADMIN_RATE_INHOUSE,
  materialAdminRate: MATERIAL_ADMIN_RATE,
  indirectExpenseRatio: CONSTANTS.indirectExpenseRatio,
};

export interface CncSettingsResource extends Resource<CncSettings> {
  save: (patch: Partial<CncSettings>) => Promise<CncSettings>;
}

export function useCncSettings(): CncSettingsResource {
  const res = useResource(api.getCncSettings, CNC_SETTINGS_FALLBACK);
  return {
    ...res,
    save: (patch) => withReload(api.updateCncSettings(patch), res.reload),
  };
}

// ----- Misc reads -----------------------------------------------------------
export function useLossReasons(): Resource<string[]> {
  return useResource(api.getLossReasons, []);
}

export function useMonthly(): Resource<MonthlyPoint[]> {
  return useResource(api.getMonthly, []);
}

// ----- Cost master ----------------------------------------------------------
// Seed the resource with the static mirror so the screen renders instantly even
// before/without the API; the fetch then refreshes from the server's copy.
const COST_MASTER_FALLBACK: api.CostMaster = {
  // 임율은 공정 행 컬럼으로 통합 — 정적 임율 테이블에서 채운다.
  // 노출순서 0 = 미지정 → 분해표는 통합 전 고정 규칙으로 fallback(서버 응답 오면 대체).
  processes: PROCESSES.map((p) => ({ ...p, laborRate: laborRateOf(p.key), displayOrder: 0 })),
  densities: [...DENSITIES],
  constants: CONSTANTS,
  // 서버 응답 전 빈 상태 — 원재료/표면처리/견적설정은 서버(흡수 결과)가 원본.
  rawMaterials: [],
  surfaceTreatments: [],
  quoteSettings: {
    allowance: DEFAULT_ALLOWANCE,
    generalAdminRate: GENERAL_ADMIN_RATE_INHOUSE,
    materialAdminRate: MATERIAL_ADMIN_RATE,
  },
};

export interface CostMasterResource extends Resource<api.CostMaster> {
  save: (master: api.CostMaster) => Promise<api.CostMaster>;
}

export function useCostMaster(): CostMasterResource {
  const res = useResource(api.getCostMaster, COST_MASTER_FALLBACK);
  return {
    ...res,
    save: (master) => withReload(api.updateCostMaster(master), res.reload),
  };
}

export interface CompanySealResource extends Resource<string> {
  save: (file: File) => Promise<string>;
}

// 회사 직인 — 오브젝트 스토리지 업로드, 표시는 blob URL("" = 미설정). 한 번 저장하면 영구 유지.
// ponytail: reload 시 이전 blob URL 은 revoke 하지 않음 — 직인 1장, 세션당 몇 번 수준의 누수라 무시.
export function useCompanySeal(): CompanySealResource {
  const res = useResource(api.fetchCompanySealUrl, "");
  return {
    ...res,
    save: (file) => withReload(api.uploadCompanySeal(file), res.reload),
  };
}

// 스토리지 키/외부 URL → <img src> 로 쓸 수 있는 URL. 외부(data:/http:/blob:) 값은
// 그대로, 스토리지 키는 인증 fetch 후 blob URL(언마운트·키 변경 시 revoke). "" → null.
export function useQuoteImageSrc(ref: string): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!ref) {
      setUrl(null);
      return;
    }
    if (/^(data:|https?:|blob:)/.test(ref)) {
      setUrl(ref);
      return;
    }
    let disposed = false;
    let blobUrl: string | null = null;
    api
      .fetchQuoteFileUrl(ref)
      .then((u) => {
        blobUrl = u;
        // 언마운트 후 도착한 blob 은 즉시 해제(누수 방지).
        if (disposed) URL.revokeObjectURL(u);
        else setUrl(u);
      })
      .catch(() => setUrl(null));
    return () => {
      disposed = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [ref]);
  return url;
}

export function usePriceHistory(item: string | null): Resource<PriceHistoryPoint[]> {
  const fetcher = useCallback(
    () => (item ? api.getPriceHistory(item) : Promise.resolve([])),
    [item]
  );
  return useResource(fetcher, []);
}
